import { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/lib/api";
import GameCard from "@/components/GameCard";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";

const TOP_LIMIT = 6;

const Games = () => {
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const res = await apiClient.get("/games");
                if (mounted) setGames(res.data.games || []);
            } catch (e) {
                if (mounted) setError("Could not load games right now.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        const t = setInterval(load, 30000);
        return () => { mounted = false; clearInterval(t); };
    }, []);

    // Sort by peakCcu descending. Games without a peak sort to the bottom.
    const sortedGames = useMemo(
        () => [...games].sort((a, b) => (b.peakCcu ?? -1) - (a.peakCcu ?? -1)),
        [games]
    );

    const hasMore = sortedGames.length > TOP_LIMIT;
    const visible = showAll ? sortedGames : sortedGames.slice(0, TOP_LIMIT);
    const hiddenCount = sortedGames.length - TOP_LIMIT;

    return (
        <div data-testid="games-page" className="pt-28 pb-24">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="max-w-2xl fade-up">
                    <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-slate-400">
                        Deep Current Portfolio
                    </span>
                    <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight">
                        Our <span className="text-teal-400">games</span>
                    </h1>
                    <p className="mt-4 text-slate-300 text-base md:text-lg leading-relaxed">
                        Every card links straight to Roblox. Player counts and visits refresh live from the Roblox API.
                    </p>
                </div>

                {error && (
                    <p data-testid="games-error" className="mt-8 text-sm text-amber-400 font-mono">{error}</p>
                )}

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {loading
                        ? Array.from({ length: 3 }).map((_, i) => (
                              <div key={i} className="rounded-2xl border border-white/10 bg-[#0F0A14] aspect-[16/12] animate-pulse" />
                          ))
                        : visible.map((g, i) => <GameCard key={g.placeId} game={g} index={i} />)}
                </div>

                {!loading && hasMore && (
                    <div className="mt-10 flex justify-center">
                        <Button
                            variant="outline"
                            size="lg"
                            data-testid="games-toggle-more"
                            onClick={() => setShowAll((v) => !v)}
                            className="rounded-full h-11 px-6 border-teal-500/30 bg-teal-500/5 text-teal-300 hover:bg-teal-500/10 hover:text-teal-200"
                        >
                            {showAll ? (
                                <>
                                    Show fewer <ChevronUp className="w-4 h-4" />
                                </>
                            ) : (
                                <>
                                    Show {hiddenCount} more game{hiddenCount === 1 ? "" : "s"}
                                    <ChevronDown className="w-4 h-4" />
                                </>
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Games;
